import { useEffect, useRef, useState } from "react";
import { GOOGLE_MAPS_ENABLED } from "../../../config/googleMaps";
import { useGoogleMapsPlaces } from "../GoogleMaps/GoogleMapsContext";

const BOUNDARY_STYLE = {
  fillColor: "#cc4645",
  fillOpacity: 0.35,
  strokeColor: "#cc4645",
  strokeWeight: 3,
  strokeOpacity: 1,
};

function getFeaturePlaceId(options) {
  const feature = options?.feature;
  if (!feature) return "";
  // Public API. Legacy minified prop kept as fallback for older Maps builds.
  return feature.placeId || feature.nh || "";
}

function applyLocalityStyle(layer, placeIds = []) {
  if (!layer) return;

  const targets = placeIds.filter(Boolean);
  if (!targets.length) {
    layer.style = null;
    return;
  }

  layer.style = (options) => {
    const featurePlaceId = getFeaturePlaceId(options);
    if (targets.includes(featurePlaceId)) {
      return BOUNDARY_STYLE;
    }
    return null;
  };
}

function findLocalityPlaceId(results = []) {
  for (const result of results) {
    const types = result.types || [];
    if (types.includes("locality") || types.includes("postal_town")) {
      return result.place_id || "";
    }
  }
  return "";
}

async function ensureMapsConstructor() {
  if (typeof window.google?.maps?.Map === "function") return true;

  if (typeof window.google?.maps?.importLibrary === "function") {
    try {
      await window.google.maps.importLibrary("maps");
    } catch {
      return false;
    }
  }

  return typeof window.google?.maps?.Map === "function";
}

function MapComponent({ placeId, lat, lng }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const layerRef = useRef(null);
  const { placesEnabled } = useGoogleMapsPlaces();
  const [mapsReady, setMapsReady] = useState(
    () => typeof window.google?.maps?.Map === "function",
  );

  useEffect(() => {
    if (!GOOGLE_MAPS_ENABLED || !placesEnabled) return undefined;

    let cancelled = false;

    ensureMapsConstructor().then((ready) => {
      if (!cancelled) setMapsReady(ready);
    });

    return () => {
      cancelled = true;
    };
  }, [placesEnabled]);

  useEffect(() => {
    if (
      !GOOGLE_MAPS_ENABLED ||
      !placesEnabled ||
      !mapsReady ||
      !mapRef.current ||
      typeof window.google?.maps?.Map !== "function"
    ) {
      return undefined;
    }

    let cancelled = false;
    const center = {
      lat: Number(lat) || 37.7749,
      lng: Number(lng) || -122.4194,
    };

    const map = new window.google.maps.Map(mapRef.current, {
      center,
      zoom: 10,
      mapId: "3958512659c8ba519f6df3e3",
    });

    mapInstance.current = map;

    let layer = null;
    try {
      layer = map.getFeatureLayer(
        window.google.maps.FeatureType?.LOCALITY || "LOCALITY",
      );
    } catch {
      layer = null;
    }
    layerRef.current = layer;

    const styleBoundary = async () => {
      const ids = [];
      if (placeId) ids.push(placeId);

      // LOCALITY layer only matches locality place IDs. Resolve one from the
      // selected coordinates so the red city outline still appears when the
      // stored placeId is missing or not a locality.
      if (
        typeof window.google.maps.Geocoder === "function" &&
        Number.isFinite(center.lat) &&
        Number.isFinite(center.lng)
      ) {
        try {
          const geocoder = new window.google.maps.Geocoder();
          const response = await geocoder.geocode({ location: center });
          if (cancelled) return;
          const localityId = findLocalityPlaceId(response?.results || []);
          if (localityId && !ids.includes(localityId)) {
            ids.push(localityId);
          }
        } catch {
          // Keep any placeId we already have.
        }
      }

      if (cancelled) return;
      applyLocalityStyle(layerRef.current, ids);
    };

    styleBoundary();

    // Feature layers can become available slightly after map creation.
    const retryTimer = window.setTimeout(() => {
      if (!cancelled) styleBoundary();
    }, 500);

    return () => {
      cancelled = true;
      window.clearTimeout(retryTimer);
      if (layerRef.current) {
        layerRef.current.style = null;
      }
      layerRef.current = null;
      mapInstance.current = null;
    };
  }, [lat, lng, placeId, placesEnabled, mapsReady]);

  if (!GOOGLE_MAPS_ENABLED) return null;

  return (
    <div
      ref={mapRef}
      style={{ height: "500px", width: "100%" }}
      aria-label="Service area map"
    />
  );
}

export default MapComponent;
export { MapComponent };
